import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { usePermissions } from '../context/PermissionsContext';

interface BrokerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  brokerId?: number | null;
  readOnly?: boolean;
}

const BrokerDetailsModal: React.FC<BrokerDetailsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  brokerId,
  readOnly = false
}) => {
  const [brokerDetails, setBrokerDetails] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    company_name: '',
    broker_number: '',
    status: 'active',
    inception_date: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const { can } = usePermissions();
  const [error, setError] = useState('');
  const isEditMode = !!brokerId;
  const canWrite = can('brokerage_details', 'write');
const isViewMode = (readOnly && isEditMode) || !canWrite;
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && brokerId) {
        fetchBrokerDetails(brokerId);
            } else {
        setBrokerDetails({
          name: '',
          surname: '',
          email: '',
          phone: '',
          company_name: '',
          broker_number: '',
          status: 'active',
          inception_date: ''
        });
        setError('');
        setExistingLogoUrl(null);
        setLogoFile(null);
        setLogoPreviewUrl(null);
        setRemoveLogo(false);
      }
    }
  }, [isOpen, isEditMode, brokerId]);

  const fetchBrokerDetails = async (id: number) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/api/brokers/${id}`);
      const data = response.data;

            setBrokerDetails({
        name: data.name || '',
        surname: data.surname || '',
        email: data.email || '',
        phone: data.phone || '',
        company_name: data.company_name || '',
        broker_number: data.broker_number || '',
        status: data.status || 'active',
        inception_date: data.inception_date ? data.inception_date.split('T')[0] : ''
      });

      setExistingLogoUrl(data.logo_url || null);
      setLogoFile(null);
      setLogoPreviewUrl(null);
      setRemoveLogo(false);
    } catch (err: any) {
      console.error('Error fetching broker details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load broker details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBrokerDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be 2MB or smaller');
      return;
    }
    setError('');
    setLogoFile(file);
    setRemoveLogo(false);

    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveLogo = () => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setRemoveLogo(true);
  };

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) return;

    setIsLoading(true);
    setError('');

    try {
      const url = isEditMode ? `/api/brokers/${brokerId}` : `/api/brokers`;
      const method = isEditMode ? 'put' : 'post';

      const formData = new FormData();
      Object.entries(brokerDetails).forEach(([key, value]) => {
        formData.append(key, value == null ? '' : String(value));
      });

      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (isEditMode && removeLogo) {
        formData.append('remove_logo', 'true');
      }

      const response = await axiosInstance[method](url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data;

      if (!isEditMode && data.temporaryPassword) {
        alert(
          `Broker created successfully!\n\n` +
          `Broker Admin Login Credentials:\n` +
          `Email: ${brokerDetails.email}\n` +
          `Temporary Password: ${data.temporaryPassword}\n\n` +
          `Please share these credentials with the broker. They can change their password after first login.`
        );
      } else {
        alert(`Broker ${isEditMode ? 'updated' : 'created'} successfully!`);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving broker details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save broker details');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const title = isViewMode ? 'View Broker' : isEditMode ? 'Edit Broker' : 'Add New Broker';
  const subtitle = isViewMode
    ? 'Broker information (read-only)'
    : isEditMode
      ? 'Update broker information'
      : 'Enter new broker information';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              &times;
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brokerage Name *
              </label>
              <input
                type="text"
                name="company_name"
                value={brokerDetails.company_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                required={!isViewMode}
                disabled={isLoading || isViewMode}
              />
            </div>

                        <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Broker Logo
              </label>
              <div className="flex items-start gap-4">
                <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                  {logoPreviewUrl ? (
                    <img src={logoPreviewUrl} alt="New logo preview" className="max-w-full max-h-full object-contain" />
                  ) : existingLogoUrl && !removeLogo ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL || ''}${existingLogoUrl}`}
                      alt="Current logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 text-center px-2">No logo</span>
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoSelect}
                    disabled={isLoading || isViewMode}
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500">
                    JPG, PNG, WEBP, or SVG. Max 2MB. Displays in the sidebar for this broker's users.
                  </p>
                  {!isViewMode && (logoFile || (existingLogoUrl && !removeLogo)) && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      disabled={isLoading}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Broker Number
              </label>
              <input
                type="text"
                name="broker_number"
                value={brokerDetails.broker_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isLoading || isViewMode}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact First Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={brokerDetails.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={isLoading || isViewMode}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Surname
                </label>
                <input
                  type="text"
                  name="surname"
                  value={brokerDetails.surname}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={isLoading || isViewMode}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={brokerDetails.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={isLoading || isViewMode}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={brokerDetails.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={isLoading || isViewMode}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inception Date
              </label>
              <input
                type="date"
                name="inception_date"
                value={brokerDetails.inception_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isLoading || isViewMode}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={brokerDetails.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                disabled={isLoading || isViewMode}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
              >
                {isLoading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrokerDetailsModal;